import {BACKEND_URL, PUBLIC_URL} from '../config';

const RECOVERY_LOCATION_KEY = 'indekso.auth.recovery-location';
const RECOVERY_LOCATION_MAX_AGE_MS = 30 * 60 * 1000;

interface RecoveryLocation {
    pathname: string;
    search: string;
    hash: string;
    createdAt: number;
}

let loginInProgress = false;

const getBasePath = (): string => {
    const normalized = PUBLIC_URL.startsWith('/') ? PUBLIC_URL : `/${PUBLIC_URL}`;
    return normalized.length > 1 && normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
};

const isPathWithinBasePath = (pathname: string): boolean => {
    const basePath = getBasePath();
    return basePath === '/' || pathname === basePath || pathname.startsWith(`${basePath}/`);
};

const isValidRecoveryLocation = (value: unknown): value is RecoveryLocation => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const location = value as RecoveryLocation;
    if (!(typeof location.pathname === 'string' &&
        typeof location.search === 'string' &&
        typeof location.hash === 'string' &&
        typeof location.createdAt === 'number' &&
        Number.isFinite(location.createdAt) &&
        Date.now() - location.createdAt >= 0 &&
        Date.now() - location.createdAt <= RECOVERY_LOCATION_MAX_AGE_MS &&
        location.pathname.startsWith('/') &&
        isPathWithinBasePath(location.pathname))) {
        return false;
    }

    const url = new URL(location.pathname + location.search + location.hash, window.location.origin);
    return url.origin === window.location.origin;
};

export const clearPendingAuthRecovery = (): void => {
    sessionStorage.removeItem(RECOVERY_LOCATION_KEY);
};

const saveCurrentLocationForRecovery = (): void => {
    const {pathname, search, hash} = window.location;
    if (!isPathWithinBasePath(pathname)) {
        return;
    }

    sessionStorage.setItem(RECOVERY_LOCATION_KEY, JSON.stringify({pathname, search, hash, createdAt: Date.now()}));
};

export const consumePendingAuthRecovery = (): string | null => {
    const storedValue = sessionStorage.getItem(RECOVERY_LOCATION_KEY);
    clearPendingAuthRecovery();

    if (!storedValue) {
        return null;
    }

    try {
        const location: unknown = JSON.parse(storedValue);
        if (!isValidRecoveryLocation(location)) {
            return null;
        }
        return location.pathname + location.search + location.hash;
    } catch (_) {
        return null;
    }
};

export const restorePendingAuthRecovery = (): boolean => {
    const location = consumePendingAuthRecovery();
    if (!location) {
        return false;
    }

    window.location.replace(location);
    return true;
};

export const beginKeycloakLogin = (): void => {
    if (loginInProgress) {
        return;
    }

    loginInProgress = true;
    saveCurrentLocationForRecovery();
    window.location.assign((BACKEND_URL || '') + '/oauth2/authorization/keycloak');
};

export const resetLoginInProgressForTests = (): void => {
    loginInProgress = false;
};
